import Input from '@/components/Input';
import Select from '@/components/Select';
import RequiredLabel from '@/components/auth/RequiredLabel';
import { LuX } from 'react-icons/lu';
import type { CandidateFormItemFormType, CandidateFormItemFormatType } from '@/types/serializers';
import {
  candidateFormItemFormatTypeOptions,
  candidateFormItemFormTypeOptions,
} from '@/features/agent/form/candidateFormItemOptions';

type FormType = CandidateFormItemFormType;
type FormatType = CandidateFormItemFormatType;

type Props = {
  title: string;
  required: boolean;
  formType: FormType;
  description: string;
  placeholder: string;
  suffix: string;
  formatType: FormatType;
  options: string[];
  minLength: string;
  maxLength: string;
  onTitleChange: (value: string) => void;
  onRequiredChange: (value: boolean) => void;
  onFormTypeChange: (value: FormType) => void;
  onDescriptionChange: (value: string) => void;
  onPlaceholderChange: (value: string) => void;
  onSuffixChange: (value: string) => void;
  onFormatTypeChange: (value: FormatType) => void;
  onOptionsChange: (value: string[]) => void;
  onMinLengthChange: (value: string) => void;
  onMaxLengthChange: (value: string) => void;
};

function Toggle({ checked, onChange }: { checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative h-5 w-10 rounded-full transition-colors duration-200 focus:ring-2 focus:ring-offset-2 focus:outline-hidden ${
        checked ? 'bg-primary' : 'bg-gray-300'
      } cursor-pointer`}
    >
      <span
        className={`absolute top-0.5 left-0.5 size-4 transform rounded-full bg-white transition-transform duration-200 ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

export default function AddItemManual({
  title,
  required,
  formType,
  description,
  placeholder,
  suffix,
  formatType,
  options,
  minLength,
  maxLength,
  onTitleChange,
  onRequiredChange,
  onFormTypeChange,
  onDescriptionChange,
  onPlaceholderChange,
  onSuffixChange,
  onFormatTypeChange,
  onOptionsChange,
  onMinLengthChange,
  onMaxLengthChange,
}: Props) {
  const isTextOrTextarea = formType === 'text' || formType === 'textarea';
  const isShowOptions = formType === 'selectbox' || formType === 'radio' || formType === 'checkbox';
  // EditCard と同じ: text, textarea, selectbox のときのみプレースホルダー表示（radio/checkbox では非表示）
  const isShowPlaceholder =
    formType === 'text' || formType === 'textarea' || formType === 'selectbox';
  const maxLengthLimit = formType === 'textarea' ? '2000' : '150';
  const maxLengthPlaceholder = formType === 'textarea' ? '2000' : '150';

  const addOption = () => {
    onOptionsChange([...options, '']);
  };

  const removeOption = (index: number) => {
    onOptionsChange(options.filter((_, i) => i !== index));
  };

  const updateOption = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    onOptionsChange(newOptions);
  };
  return (
    <div>
      <div className="flex items-center gap-2">
        <label className="text-sm font-bold text-gray-900">質問文</label>
        <RequiredLabel />
      </div>
      <Input
        type="text"
        value={title}
        onChange={(e) => onTitleChange(e.target.value)}
        placeholder="質問文を入力してください"
        className="mt-2 w-full"
      />

      <div className="mt-6 flex gap-6">
        {/* 左側カラム */}
        <div className="flex w-[236px] flex-col gap-5">
          <div className="flex items-center gap-4">
            <label className="w-[70px] text-sm font-bold text-gray-900">必須項目</label>
            <Toggle checked={required} onChange={onRequiredChange} />
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <label className="text-sm font-bold text-gray-900">情報タイプ</label>
              <RequiredLabel />
            </div>
            <Select
              items={candidateFormItemFormTypeOptions}
              value={formType}
              onValueChange={(value) => onFormTypeChange(value as FormType)}
              className="w-full"
            />
          </div>
          {/* 最小文字数・最大文字数（EditCard と同じく text/textarea のときのみ表示） */}
          {isTextOrTextarea && (
            <>
              <div className="flex items-center gap-4">
                <label className="w-[70px] text-sm font-bold text-gray-900">最小文字数</label>
                <Input
                  type="number"
                  value={minLength}
                  onChange={(e) => {
                    const newMinLength = e.target.value;
                    onMinLengthChange(newMinLength);
                    if (
                      newMinLength &&
                      maxLength &&
                      parseInt(newMinLength, 10) > parseInt(maxLength, 10)
                    ) {
                      onMaxLengthChange(newMinLength);
                    }
                  }}
                  placeholder="0"
                  className="w-[140px]"
                  min="0"
                  max={maxLengthLimit}
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="w-[70px] text-sm font-bold text-gray-900">最大文字数</label>
                <Input
                  type="number"
                  value={maxLength}
                  onChange={(e) => {
                    const newMaxLength = e.target.value;
                    onMaxLengthChange(newMaxLength);
                    if (
                      newMaxLength &&
                      minLength &&
                      parseInt(newMaxLength, 10) < parseInt(minLength, 10)
                    ) {
                      onMinLengthChange(newMaxLength);
                    }
                  }}
                  placeholder={maxLengthPlaceholder}
                  className="w-[140px]"
                  min="0"
                  max={maxLengthLimit}
                />
              </div>
            </>
          )}
        </div>

        {/* 右側カラム */}
        <div className="flex flex-1 flex-col gap-4 border-l border-solid border-[#d1d1d1] pl-6">
          {isShowOptions && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-bold text-gray-900">選択肢</label>
                <button
                  type="button"
                  onClick={addOption}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  + 選択肢を追加
                </button>
              </div>
              <div className="space-y-2">
                {options.map((option, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      type="text"
                      value={option}
                      onChange={(e) => updateOption(index, e.target.value)}
                      placeholder={`選択肢${index + 1}`}
                      className="flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => removeOption(index)}
                      className="rounded p-1 hover:bg-gray-100"
                    >
                      <LuX size={16} className="text-gray-600" />
                    </button>
                  </div>
                ))}
                {options.length === 0 && (
                  <p className="text-sm text-gray-500">選択肢を追加してください</p>
                )}
              </div>
            </div>
          )}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-bold text-gray-900">注釈</label>
            <Input
              type="text"
              value={description}
              onChange={(e) => onDescriptionChange(e.target.value)}
              placeholder="質問文の下に表示する補足説明を入力してください"
              maxLength={50}
              className="w-full"
            />
            <p className="text-sm text-gray-500">50文字以内</p>
          </div>
          {isShowPlaceholder && (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-bold text-gray-900">プレースホルダー</label>
              <Input
                type="text"
                value={placeholder}
                onChange={(e) => onPlaceholderChange(e.target.value)}
                placeholder="プレースホルダーを入力してください"
                className="w-full"
              />
            </div>
          )}
          {/* フォーマット制限・フォーム末尾は EditCard と同じく text のときのみ */}
          {formType === 'text' && (
            <>
              <div className="flex w-[236px] flex-col gap-2">
                <label className="text-sm font-bold text-gray-900">フォーマット制限</label>
                <Select
                  items={candidateFormItemFormatTypeOptions}
                  value={formatType}
                  onValueChange={(value) => onFormatTypeChange(value as FormatType)}
                  className="w-full"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-bold text-gray-900">フォーム末尾</label>
                <Input
                  type="text"
                  value={suffix}
                  onChange={(e) => onSuffixChange(e.target.value)}
                  placeholder="フォームの末尾に文字を入れる場合は入力してください（万円など）"
                  className="w-full"
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
